import { Option } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { TimePicker, TimeSelectionData, TimeSelectionEvents } from '@fluentui/react-timepicker-compat';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { TimeZone } from '../../../models';
import { SelectOptions } from '../form';
import './DateTimePicker.scss';

interface DateTimePickerProps {
  dateTime?: string;
  disabled?: boolean;
  dateLabel?: string;
  timeLabel?: string;
  datePlaceholder?: string;
  timeZones?: TimeZone[];
  timeZone?: string;
  onDateTimeChange: (dateTime?: Date) => void;
  onTimeZoneChange?: (timeZone: string) => void;
}

const DateTimePickerImpl: React.FC<DateTimePickerProps> = (props) => {
  const {
    dateTime,
    disabled,
    dateLabel = 'Date',
    timeLabel = 'Time',
    datePlaceholder = 'Select a date',
    timeZones = [],
    timeZone,
    onDateTimeChange,
    onTimeZoneChange,
  } = props;

  const getInitialDateTime = (): Date | undefined => {
    if (!dateTime) return undefined;

    const parsedDateTime = new Date(dateTime);
    return Number.isNaN(parsedDateTime.getTime()) ? undefined : parsedDateTime;
  };

  const initialDateTime = getInitialDateTime();
  const initialTime = initialDateTime ?? new Date();

  const [date, setDate] = useState<Date | null>(initialDateTime ?? null);
  const [time, setTime] = useState<Date>(initialTime);
  const [timeText, setTimeText] = useState<string>(format(initialTime, 'h:mm'));
  const [selectedAmPm, setSelectedAmPm] = useState(initialTime.getHours() < 12 ? 'AM' : 'PM');

  const applyDateTime = (nextDate: Date, nextTime: Date): void => {
    const mergedDateTime = new Date(nextDate);
    mergedDateTime.setHours(nextTime.getHours(), nextTime.getMinutes(), 0, 0);
    setDate(mergedDateTime);
    setTime(mergedDateTime);
    setTimeText(format(mergedDateTime, 'h:mm'));
    setSelectedAmPm(mergedDateTime.getHours() < 12 ? 'AM' : 'PM');
    onDateTimeChange(mergedDateTime);
  };

  const handleDateChange = (selectedDate: Date | null | undefined): void => {
    if (!selectedDate) {
      setDate(null);
      onDateTimeChange(undefined);
      return;
    }

    applyDateTime(selectedDate, time);
  };

  const handleTimeChange = (_event: TimeSelectionEvents, data: TimeSelectionData): void => {
    if (!data.selectedTime || !date) return;

    const selectedTimeText = data.selectedTimeText?.toUpperCase();
    let nextAmPm = selectedAmPm;
    if (selectedTimeText?.includes('AM')) nextAmPm = 'AM';
    if (selectedTimeText?.includes('PM')) nextAmPm = 'PM';

    const adjustedTime = new Date(data.selectedTime);
    const hours = adjustedTime.getHours();

    if (nextAmPm === 'AM' && hours >= 12) {
      adjustedTime.setHours(hours - 12);
    } else if (nextAmPm === 'PM' && hours < 12) {
      adjustedTime.setHours(hours + 12);
    }

    applyDateTime(date, adjustedTime);
  };

  const handleTimeInput = (event: React.FormEvent<HTMLInputElement>): void => {
    setTimeText(event.currentTarget.value);
  };

  const handleSelectedAmPmChange = (optionValue?: string): void => {
    if ((optionValue !== 'AM' && optionValue !== 'PM') || !date) return;

    const adjustedTime = new Date(time);
    const hours = adjustedTime.getHours();

    if (optionValue === 'AM' && hours >= 12) {
      adjustedTime.setHours(hours - 12);
    } else if (optionValue === 'PM' && hours < 12) {
      adjustedTime.setHours(hours + 12);
    }

    applyDateTime(date, adjustedTime);
  };

  useEffect(() => {
    if (!dateTime) {
      const resetTime = new Date();

      setDate(null);
      setTime(resetTime);
      setTimeText('');
      setSelectedAmPm(resetTime.getHours() < 12 ? 'AM' : 'PM');
      return;
    }

    const nextDate = new Date(dateTime);
    if (Number.isNaN(nextDate.getTime())) return;

    setDate(nextDate);
    setTime(nextDate);
    setTimeText(format(nextDate, 'h:mm'));
    setSelectedAmPm(nextDate.getHours() < 12 ? 'AM' : 'PM');
  }, [dateTime]);

  return (
    <div className='date-time-picker'>
      <DatePicker
        aria-label={dateLabel}
        className='date-time-picker__date'
        allowTextInput
        value={date}
        onSelectDate={handleDateChange}
        formatDate={(selectedDate) => selectedDate ? format(selectedDate, 'MM/dd/yy') : ''}
        placeholder={datePlaceholder}
        disabled={disabled}
      />
      <TimePicker
        freeform
        aria-label={timeLabel}
        className='date-time-picker__time'
        selectedTime={time}
        value={timeText}
        increment={15}
        onInput={handleTimeInput}
        onTimeChange={handleTimeChange}
        disabled={disabled}
      />
      <SelectOptions
        className='date-time-picker__am-pm'
        dropdownProps={{
          'aria-label': 'AM or PM selector',
          inlinePopup: true,
          selectedOptions: [selectedAmPm],
          value: selectedAmPm,
          disabled,
        }}
        onOptionSelect={(data) => handleSelectedAmPmChange(data.optionValue)}
      >
        <Option value='AM'>AM</Option>
        <Option value='PM'>PM</Option>
      </SelectOptions>
      {timeZones.length > 0 && (
        <SelectOptions
          className='date-time-picker__timezone'
          dropdownProps={{
            'aria-label': 'Timezone selector',
            selectedOptions: [timeZone ?? ''],
            value: timeZone ?? '',
            disabled,
          }}
          onOptionSelect={(data) => data.optionValue && onTimeZoneChange?.(data.optionValue)}
        >
          {timeZones.map((availableTimeZone) => (
            <Option
              key={availableTimeZone.displayName}
              value={availableTimeZone.displayName}
            >
              {`(UTC${availableTimeZone.utcOffset}) - ${availableTimeZone.displayName}`}
            </Option>
          ))}
        </SelectOptions>
      )}
    </div>
  );
};

export const DateTimePicker = observer(DateTimePickerImpl);
